import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import {
  AlertController,
  LoadingController,
  ModalController,
} from "ionic-angular";
import moment from "moment";
import { Subscription } from "rxjs";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import { RnBRates } from "../../../../models/payers/payer";
import { stateList } from "../../../../pages/patient-chart/patient-benefits/models";
import { PayersService } from "../../../../providers/payers-service/payers.service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import { NgbCustomParserFormatter } from "../../../common/date-picker/ngb-custom-parser-formatter";
import {
  ContractPeriod,
  Options,
  RoomAndBoardRate,
  RoomAndBoardRates,
} from "../models";

@Component({
  selector: "rates-rnb",
  templateUrl: "./rates-rnb.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatesRnb),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RatesRnb),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RatesRnb implements OnInit, OnDestroy {
  ratesRnbFormGroup: FormGroup;
  contractPeriodsOptions: ContractPeriod[];
  stateOptions: Options[];
  selectedStates: Options[];
  stateOptionsPreloaded: Options[];
  periodSelected: boolean = false;
  sortOptions: SortOptions = new SortOptions("state", "asc");
  editMode: boolean = false;
  readonly: boolean = false;
  _saveEnabled: boolean = false;
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  minDateFormat: any = moment("2023-09-30", this.dateFormat);
  stateList = stateList;
  addNewContractPeriodOption: ContractPeriod;
  payerId: string;
  payerType: string;
  benefitType: string = "ROOMANDBOARD";
  previousContractPeriodSelected: ContractPeriod;
  subscriptions: Subscription[] = [];
  rnbRates: RnBRates[] = [];
  rnbRatesPreloaded: RnBRates[] = [];
  rnbRatesPreviousSelected: RnBRates[] = [];
  roomAndBoardRatesData: RoomAndBoardRates;

  // CO-2315 Round-2 item #7 / Payer Foundation F5.S3. True when this payer's room-and-board
  // rates must not be configurable at the payer level at all, because the facility's
  // effective-dated rate is the sole room-and-board pricing source for these patients.
  californiaMediCalSupportToggle: boolean = false;

  @Input() currentPayerType: any;
  @Input() currentPayer: any;
  @Output() onRatesSaved = new EventEmitter<any>();
  @Output() onCanceled = new EventEmitter<any>();
  @Input() isActive: boolean = false;
  @Input() isEditMode: boolean = false;

  constructor(
    private payersService: PayersService,
    private modalCtrl: ModalController,
    protected formBuilder: FormBuilder,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private stateManagementService: StateManagementService
  ) {}

  /**
   * CO-2315 Round-2 item #7 (Payer Foundation F5.S3): the Room and Board sub-tab carries no
   * payer-level flat per-state rate for a Medi-Cal payer.
   *
   * BOTH halves of the condition are required, and the second one is the important one.
   *
   * F5.S3's own wording is "Medicaid-classified payers", and implementing that literally here
   * would hide the rate table from every Ohio, CareSource, Molina, Anthem, UHC Community Plan,
   * Wellpoint, Wyoming, Indiana and South Carolina Medicaid payer in the fleet — payers that
   * price 1,021 live room-and-board benefits off exactly this field (measured across all
   * 45,145 production benefit rows, 2026-09-04; none of them Californian). Their billers would
   * lose the only screen where that rate is entered, while the backend went on pricing from
   * rows they could no longer see or correct.
   *
   * So the tenant's Medi-Cal enablement gates it, exactly as the backend predicate does
   * (revenue-line-item-functions IsMediCalRBPayerRateBlocked). The two must agree: a screen
   * that hides the field while the backend still prices from it is worse than either
   * behaviour alone, because the configuration becomes invisible rather than absent.
   */
  get isPayerLevelRnbRateSuppressed(): boolean {
    if (!this.californiaMediCalSupportToggle) {
      return false;
    }
    const payerType = (this.currentPayer && this.currentPayer.payerType) || "";
    if (payerType.toString().trim().toUpperCase() !== "MEDICAID") {
      return false;
    }
    // PRD 2 REQ-002's own condition is `PayerType = Medicaid AND state = CA`, and the
    // pricing path enforces exactly that (IsMediCalRBPayerRateBlocked). This screen must
    // agree with it. A tab that hides the rate table while the backend goes on pricing from
    // that table is worse than either behaviour alone: the configuration becomes invisible
    // rather than absent, and nobody can see or correct the rate that is being used.
    //
    // An absent state means "not a Medi-Cal payer", never a default to California - 453 of
    // 453 Medicaid payer records in production carry no state today (the field is optional
    // at the source and set only by the Medi-Cal payer form), so defaulting the other way
    // would take the rate table away from every Medicaid payer in the fleet.
    const state = (this.currentPayer && this.currentPayer.state) || "";
    return state.toString().trim().toUpperCase() === "CA";
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.stateManagementService.californiaMediCalSupportToggle.subscribe(
        (enabled) => {
          this.californiaMediCalSupportToggle = !!enabled;
        }
      )
    );
    this.setReadOnly();
    this.loadOptions();
    this.initFormGroup();
  }

  onPeriodChanged() {
    // remove controls
    this.rnbRates.forEach((rate) => {
      this.removeStateFormControlRow(rate.state);
    });
    // clean data
    this.setReadOnly();
    this.loadOptions();
    this.cleanData();
    this.ratesRnbFormGroup.reset();
    this.getPayerContractPeriods();
  }

  initFormGroup() {
    this.ratesRnbFormGroup = this.formBuilder.group({});
    this.subscriptions.push(
      this.ratesRnbFormGroup.valueChanges.subscribe(this.onFormChanges)
    );
    this.ratesRnbFormGroup.addControl(
      "selectedContractPeriod",
      new FormControl(null, [
        Validators.required,
        this.notAddNewSelectedValidator(),
      ])
    );
  }

  notAddNewSelectedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = control.value != 0;
      return isValid ? null : { notZero: { value: control.value } };
    };
  }

  setReadOnly() {
    this.readonly =
      this.currentPayerType && this.currentPayerType.length > 0
        ? this.currentPayerType == "MEDICARE"
        : false;
  }

  onFormChanges = (value) => {
    if (value) {
      this.checkCanSave();
    }
  };

  checkCanSave() {
    this.saveEnabled =
      this.ratesRnbFormGroup &&
      this.ratesRnbFormGroup.valid &&
      this.periodSelected &&
      this.selectedStates.length > 0;
  }

  loadOptions(): void {
    this.stateOptions = this.stateList.map((s) => {
      return {
        name: s,
        value: s,
        disabled: false,
      };
    });

    if (!this.readonly) {
      this.addNewContractPeriodOption = {
        id: 0,
        name: "Add New",
        value: "Add New",
        effectiveDate: "",
        expiredDate: "",
      };

      this.contractPeriodsOptions = [];
      this.contractPeriodsOptions.push(this.addNewContractPeriodOption);
    }

    if (
      this.isEditMode &&
      this.currentPayer &&
      this.currentPayer.payerId != ""
    ) {
      this.getPayerTypeAndId();
      this.getPayerContractPeriods();
    }
  }

  getPayerTypeAndId() {
    this.payerId = this.currentPayer.payerId;
    this.payerType = this.currentPayer.payerType;
  }

  getPayerContractPeriods() {
    let payerId = this.payerId;
    let benefitType = this.benefitType;

    if (this.readonly) {
      payerId = "MEDICARE";
      benefitType = "ROOMANDBOARD";
    }

    this.payersService
      .getPayerRatesPeriods(payerId, benefitType, null)
      .pipe()
      .take(1)
      .subscribe((response) => {
        let previousContractPeriods: ContractPeriod[] = [];
        if (response !== null) {
          let contractPeriod = Object.values(response);
          contractPeriod.forEach((period) => {
            let name = moment(period.start).format(this.dateUIFormat);
            let expiredDate = null;
            if (period.end && period.end != "") {
              expiredDate = moment(period.end).format(this.dateUIFormat);
              name =
                name + " - " + moment(period.end).format(this.dateUIFormat);
            }
            let newPeriod = {
              id: previousContractPeriods.length + 1,
              name: name,
              value: moment(period.start).format(this.dateUIFormat),
              effectiveDate: moment(period.start).format(this.dateUIFormat),
              expiredDate: expiredDate,
            };

            let minDate = moment(this.minDateFormat).format(this.dateUIFormat);
            //compare date
            if (moment(newPeriod.effectiveDate).isAfter(moment(minDate))) {
              this.minDateFormat = newPeriod.effectiveDate;
            }

            previousContractPeriods.push(newPeriod);
          });
        }
        this.contractPeriodsOptions = this.readonly
          ? [...previousContractPeriods]
          : [this.addNewContractPeriodOption, ...previousContractPeriods];
        this.sortContractPeriodOptionsByDate();
      });
  }

  sortContractPeriodOptionsByDate() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.sort(
      (a, b) =>
        new Date(b.effectiveDate).getTime() -
        new Date(a.effectiveDate).getTime()
    );
  }

  cleanData(addNewPeriodContractAction: boolean = true) {
    // Form
    this.rnbRates.forEach((rate) => {
      this.removeStateFormControlRow(rate.state);
    });

    // States
    this.stateOptions = this.stateOptions.map((state) => {
      return { ...state, disabled: false };
    });
    this.selectedStates = [];
    this.stateOptionsPreloaded = [];

    // Rates
    this.rnbRates = [];
    this.rnbRatesPreloaded = [];

    // Flags
    this.saveEnabled = false;
    this.periodSelected = false;
    if (!this.readonly && addNewPeriodContractAction) {
      this.addNewPeriodContractAction(true);
    }
  }

  addNewPeriodContractAction(isEnable: boolean) {
    if (!isEnable) {
      this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
        (option) => option.id != 0
      );
    } else {
      if (
        !(
          this.contractPeriodsOptions.filter((option) => option.id == 0)
            .length > 0
        )
      ) {
        this.contractPeriodsOptions = [
          this.addNewContractPeriodOption,
          ...this.contractPeriodsOptions,
        ];
      }
    }
  }

  onContractPeriodClear(event: any) {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.id != 0
    );
    this.addNewPeriodContractAction(true);
  }

  onContractPeriodChange(newValue: ContractPeriod): void {
    if (!newValue) {
      this.rnbRates.forEach((rate) => {
        this.removeStateFormControlRow(rate.state);
      });
      this.cleanData();
      return;
    }

    this.previousContractPeriodSelected = newValue;
    if (newValue && newValue.id === 0) {
      if (newValue.value !== "Add New") {
        this.cleanData(false);
        this.periodSelected = true;
        return;
      }
      let contractPeriodModal = this.modalCtrl.create(
        "ContractPeriodModalComponent",
        { minDateFormat: this.minDateFormat },
        { cssClass: "contract-period-modal" }
      );
      contractPeriodModal.onDidDismiss((data) => {
        if (data) {
          this.cleanData();
          let newOption = this.formatContractPeriodModel(data);
          this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
            (option) => option.id != 0
          );
          this.contractPeriodsOptions = [
            ...this.contractPeriodsOptions,
            newOption,
          ];
          this.sortContractPeriodOptionsByDate();

          this.ratesRnbFormGroup.reset();
          this.ratesRnbFormGroup
            .get("selectedContractPeriod")
            .setValue(newOption);
          this.periodSelected = true;
        } else {
          if (this.previousContractPeriodSelected.id == 0) {
            this.periodSelected = false;
            this.ratesRnbFormGroup.get("selectedContractPeriod").setValue(null);
          } else {
            this.periodSelected = true;
            this.ratesRnbFormGroup
              .get("selectedContractPeriod")
              .setValue(this.previousContractPeriodSelected);
          }
        }
      });
      contractPeriodModal.present();
    } else if (this.isEditMode && newValue && newValue.id !== 0) {
      let spinnerText = "Loading Room and Board rates";

      const spinner = this.loadingCtrl.create({
        content: spinnerText,
      });
      spinner.present();

      let effectiveDate = moment(
        this.ratesRnbFormGroup.get("selectedContractPeriod").value.effectiveDate
      ).format(this.dateFormat);

      let payerId = this.payerId;
      let benefitType = this.benefitType;

      if (this.readonly) {
        payerId = "MEDICARE";
        benefitType = "ROOMANDBOARD";
      }

      this.payersService
        .getPayerRates(payerId, benefitType, effectiveDate, null)
        .pipe()
        .take(1)
        .subscribe(
          (response) => {
            spinner.dismiss();
            if (response !== null) {
              this.editMode = true;
              this.mapRnBRatesIntoForm(response);
            }
            this.periodSelected = true;
          },
          (error) => {
            spinner.dismiss();
            this.onSaveError(error);
          }
        );
    } else {
      this.periodSelected = false;
    }
  }

  mapRnBRatesIntoForm(rates) {
    this.cleanData();
    rates.rates.forEach((rate) => {
      this.rnbRatesPreloaded.push({ state: rate.state, isEditable: true });
      this.selectedStates.push(rate.state);
      this.addStateFormControlRow(rate.state);
      this.ratesRnbFormGroup
        .get("rate-rnb-" + rate.state)
        .setValue(this.addDecimals(rate.rate));
    });
    if (!this.readonly) {
      this.mapPreloadedStates(rates.rates);
    }
    this.rnbRates = [...this.rnbRatesPreloaded];
    this.rnbRates.sort((a, b) => a.state.localeCompare(b.state));
  }

  addDecimals(value: number): string {
    if (value % 1 !== 0) {
      return value.toString();
    }
    return value.toFixed(2);
  }

  mapPreloadedStates(states) {
    let duplicate = false;
    states.forEach((preload) => {
      this.stateOptionsPreloaded.forEach((state) => {
        if (state.name === preload.state) {
          duplicate = true;
        }
      });

      if (!duplicate) {
        let opt: Options = {
          name: preload.state,
          value: preload.state,
          disabled: true,
        };
        this.stateOptionsPreloaded.push(opt);
      }
      duplicate = false;
    });

    this.selectedStates = [...this.stateOptionsPreloaded];

    this.stateSelectionChanged();
  }

  removeStateFormControlRow(state: string) {
    this.ratesRnbFormGroup.removeControl("rate-rnb-" + state);
  }

  addStateFormControlRow(state: string) {
    this.ratesRnbFormGroup.addControl(
      "rate-rnb-" + state,
      new FormControl(null, [Validators.required])
    );
  }

  onClearAllStates() {
    this.selectedStates = [...this.stateOptionsPreloaded];

    if (this.stateOptionsPreloaded.length > 0) {
      this.disableStatesAndCounties();
    }

    if (this.selectedStates.length > 0) {
      this.stateSelectionChanged();
    }
  }

  stateSelectionChanged(): void {
    if (this.ratesRnbFormGroup.get("selectedContractPeriod").value) {
      this.addNewPeriodContractAction(true);
    }

    this.rnbRatesPreviousSelected = this.rnbRates;
    this.rnbRates = [...this.rnbRatesPreloaded];

    this.selectedStates.forEach((state) => {
      let duplicate = false;

      this.rnbRates.forEach((rate) => {
        if (rate.state == state.name) {
          duplicate = true;
        }
      });

      if (!duplicate) {
        let rnbRate: RnBRates = {
          state: state.name,
          isEditable: true,
        };
        this.rnbRates.push(rnbRate);
        this.addStateFormControlRow(rnbRate.state);
      }
    });

    this.rnbRatesPreviousSelected = this.rnbRatesPreviousSelected.filter(
      (previous) => !this.rnbRates.some((rate) => rate.state == previous.state)
    );

    this.rnbRatesPreviousSelected.forEach((rate) => {
      this.removeStateFormControlRow(rate.state);
    });

    this.rnbRates.sort((a, b) => a.state.localeCompare(b.state));

    if (this.stateOptionsPreloaded.length > 0) {
      this.disableStatesAndCounties();
    }
  }

  disableStatesAndCounties() {
    this.stateOptions = this.stateOptions.map((option) => {
      if (
        this.stateOptionsPreloaded
          .map((option) => option.value)
          .includes(option.name)
      ) {
        return { ...option, disabled: true };
      }
      return option;
    });
  }

  removeState(state: Options) {
    const selectedStates = this.selectedStates;
    this.selectedStates = selectedStates.filter(
      (selectedItem) => selectedItem.name !== state.name
    );
    this.stateSelectionChanged();
  }

  showSaveRnbRateModal() {
    if (this.editMode) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveRnbRates();
        }
      });
      modalPage.present();
    } else {
      this.saveRnbRates();
    }
  }

  saveRnbRates() {
    let spinnerText = "Saving Room And Board Rate";

    const spinner = this.loadingCtrl.create({
      content: spinnerText,
    });
    spinner.present();
    this.mapRnBRatesByContractPeriod(
      this.ratesRnbFormGroup.get("selectedContractPeriod").value
    );

    if (
      !this.roomAndBoardRatesData.payerId ||
      this.roomAndBoardRatesData.payerId.trim() === ""
    ) {
      this.getPayerTypeAndId();
      this.roomAndBoardRatesData.payerId = this.payerId;
      this.roomAndBoardRatesData.payerType = this.payerType;
    }

    this.payersService
      .postSavePayerRates(this.roomAndBoardRatesData)
      .pipe()
      .take(1)
      .subscribe(
        () => {
          spinner.dismiss();
          this.onRatesSaved.emit();
          this.onSaveSuccessAlert("Room And Board Rate Saved");
          // this.saveEnabled = false;

          // remove controls
          this.rnbRates.forEach((rate) => {
            this.removeStateFormControlRow(rate.state);
          });
          // clean data
          this.cleanData();
          this.ratesRnbFormGroup.reset();
          this.getPayerContractPeriods();
        },
        (error) => {
          spinner.dismiss();
          this.onSaveError(error);
        }
      );
  }

  private onSaveSuccessAlert = (message: string) => {
    this.showAlert({
      title: "Success",
      message: message,
    });
  };

  mapRnBRatesByContractPeriod(period: ContractPeriod) {
    let rnbRatePayload: RoomAndBoardRates;

    let effectiveDate = moment(period.effectiveDate).format(this.dateFormat);
    let expiredDate;
    if (period.expiredDate != null) {
      expiredDate = moment(period.expiredDate).format(this.dateFormat);
    }

    rnbRatePayload = {
      payerId: this.payerId,
      payerType: this.payerType,
      benefitType: "ROOMANDBOARD",
      benefitSubtype: null,
      start: effectiveDate,
      end: expiredDate,
      useDefaultCodes: false,
      rates: [],
    };

    this.rnbRates.forEach((rbRate) => {
      let newRate: RoomAndBoardRate = {
        state: rbRate.state,
        rate: +this.ratesRnbFormGroup.get("rate-rnb-" + rbRate.state).value,
      };

      rnbRatePayload.rates.push(newRate);
    });

    this.roomAndBoardRatesData = rnbRatePayload;
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get saveEnabled(): boolean {
    return this._saveEnabled;
  }

  cancel() {
    this.onCanceled.emit(true);
  }

  formatContractPeriodModel(period: any): ContractPeriod {
    let newContractPeriod = moment(new Date(period)).format(this.dateUIFormat);
    let newEffectiveDate = moment(new Date(period)).format(this.dateUIFormat);
    return {
      id: 0,
      name: newContractPeriod,
      value: newContractPeriod,
      effectiveDate: newEffectiveDate,
      expiredDate: null,
    };
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertCtrl.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  private onSaveError = (error) => {
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    } else {
      this.showAlert({ title: "Error", message: error });
    }
  };
}



// WEBPACK FOOTER //
// ./src/components/payers/rates-payer/rates-rnb/rates-rnb.ts